import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { DayPicker } from "react-day-picker";
import { format, startOfDay, startOfMonth, endOfMonth, startOfYear, subMonths, subDays, parse, isValid } from "date-fns";
import "react-day-picker/dist/style.css";
import CalendarIcon from "../../../../assets/icons/CalendarIcon.svg?react";
import RightArrowIcon from "../../../../assets/icons/RightArrowIcon.svg?react";
import ChevronIcon from '../../../../assets/icons/ChevronIcon.svg?react';
import XButton from '../../../../components/common/XButton';
import { tierSelectIconBase } from '../../../../components/ui-control/MultiSelectDropdown';

const DATE_FORMAT = "MM/dd/yyyy";
const TODAY = startOfDay(new Date());

const wrapperClasses = 'relative min-w-57.5';
const inputDisplayClasses = 'flex items-center gap-2 h-10 px-1.5 py-1.5 border border-border rounded bg-white cursor-pointer';
const inputDisplayTextClasses = 'flex items-center gap-1.5 flex-1';
const calendarDropdownClasses = 'absolute bg-surface rounded-[calc(var(--spacing)*2.5)] shadow-lg mt-2 p-2.5 z-[1000]';
const topBarClasses = 'flex items-center gap-2 bg-surface-light p-3 rounded-[calc(var(--spacing)*2.5)]';
const topBarInputClasses = 'border border-border bg-white p-1.5 rounded-md w-27.5 font-medium';
const calendarFooterClasses = 'flex justify-between mt-2.5 bg-surface-light h-10';

const PRESETS = [
    { label: "This month",  getRange: () => ({ from: startOfMonth(TODAY), to: TODAY }) },
    { label: "Last month",  getRange: () => ({ from: startOfMonth(subMonths(TODAY, 1)), to: endOfMonth(subMonths(TODAY, 1)) }) },
    { label: "This year",   getRange: () => ({ from: startOfYear(TODAY), to: TODAY }) },
];

const buildInputValues = (range) => ({
    from: range.from ? format(range.from, DATE_FORMAT) : "",
    to: range.to ? format(range.to, DATE_FORMAT) : "",
});

const DateRangePicker = ({ onRangeChange, defaultRange }) => {
    const initialRange = defaultRange?.startDate
        ? { from: defaultRange.startDate, to: defaultRange.endDate }
        : { from: subDays(TODAY, 30), to: TODAY };
    const [showCalendar, setShowCalendar] = useState(false);
    const [hoveredDay, setHoveredDay] = useState(null);
    const [range, setRange] = useState(initialRange);
    const [inputValues, setInputValues] = useState(buildInputValues(initialRange));

    const [selectionPhase, setSelectionPhase] = useState('idle');

    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);
    const lastClickedDayRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };
        if (showCalendar) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showCalendar]);

    const notifyParent = useCallback((from, to) => {
        if (from && to) onRangeChange({ startDate: from, endDate: to });
    }, [onRangeChange]);

    const handleInputChange = (rawValue, type) => {
        setInputValues((prev) => ({ ...prev, [type]: rawValue }));
        const parsed = parse(rawValue, DATE_FORMAT, new Date());
        if (!isValid(parsed)) return;
        setRange((prev) => {
            const updated = { ...prev, [type]: parsed };
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => notifyParent(updated.from, updated.to), 500);
            return updated;
        });
    };

    const handleCalendarToggle = () => {
        if (!showCalendar) setSelectionPhase('idle');
        setShowCalendar(!showCalendar);
    };

    const setPreset = (getRange) => {
        const newRange = getRange();
        setRange(newRange);
        setInputValues(buildInputValues(newRange));
        notifyParent(newRange.from, newRange.to);
        setSelectionPhase('idle');
        setShowCalendar(false);
    };

    const DayButton = useCallback(({ day, onClick: dayPickerOnClick, ...btnProps }) => (
        <XButton
            variant="close"
            {...btnProps}
            onMouseEnter={() => setHoveredDay(day.date)}
            onMouseLeave={() => setHoveredDay(null)}
            onClick={(e) => {
                lastClickedDayRef.current = day.date;
                dayPickerOnClick?.(e);
            }}
        />
    ), []);

    const hoverModifiers = useMemo(() => {
        if (!hoveredDay || !range.from || selectionPhase !== 'started') return {};
        const isForward = hoveredDay >= range.from;
        const hoverFrom = isForward ? range.from : hoveredDay;
        const hoverTo   = isForward ? hoveredDay : range.from;
        const mods = { hover_start: hoverFrom, hover_end: hoverTo };
        if (hoverFrom < hoverTo) mods.hover_middle = { after: hoverFrom, before: hoverTo };
        return mods;
    }, [range.from, hoveredDay, selectionPhase]);

    const handleRangeSelect = (newRange) => {
        if (!newRange) return;

        if (selectionPhase === 'idle') {
            const clickedDay = lastClickedDayRef.current;
            if (!clickedDay) return;
            const freshRange = { from: clickedDay, to: undefined };
            setRange(freshRange);
            setInputValues(buildInputValues(freshRange));
            setSelectionPhase('started');
            return;
        }
        setRange(newRange);
        setInputValues(buildInputValues(newRange));
        if (newRange.from && newRange.to) {
            notifyParent(newRange.from, newRange.to);
            setSelectionPhase('idle');
        }
    };

    return (
        <div ref={wrapperRef} className={wrapperClasses}>
            <div className={inputDisplayClasses} onClick={handleCalendarToggle}>
                <div className={inputDisplayTextClasses}>
                    <span>{inputValues.from}</span>
                    <RightArrowIcon />
                    <span>{inputValues.to}</span>
                </div>
                <ChevronIcon className={`${tierSelectIconBase} ${showCalendar ? "rotate-180" : ""}`} />
            </div>
            {showCalendar && (
                <div className={calendarDropdownClasses}>
                    <div className={topBarClasses}>
                        <span><CalendarIcon /></span>
                        <input
                            className={topBarInputClasses}
                            value={inputValues.from}
                            onChange={(e) => handleInputChange(e.target.value, "from")}
                        />
                        <span><RightArrowIcon /></span>
                        <input
                            className={topBarInputClasses}
                            value={inputValues.to}
                            onChange={(e) => handleInputChange(e.target.value, "to")}
                        />
                    </div>
                    <DayPicker
                        mode="range"
                        selected={range}
                        onSelect={handleRangeSelect}
                        modifiers={hoverModifiers}
                        modifiersClassNames={{ hover_middle: "day-hover-middle" }}
                        components={{ DayButton }}
                    />
                    <div className={calendarFooterClasses}>
                        {PRESETS.map(({ label, getRange }) => (
                            <XButton key={label} variant="close" onClick={() => setPreset(getRange)}>{label}</XButton>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
